'use client'

import {
  type CellClickedEvent,
  type ColDef,
  type ColumnMovedEvent,
  type ColumnResizedEvent,
  type CsvExportParams,
  type GetRowIdParams,
  type GridApi,
  type GridReadyEvent,
  type ICellRendererParams,
  type IRowNode,
  provideGlobalGridOptions,
  setupAgTestIds,
} from 'ag-grid-community'
import {
  AllEnterpriseModule,
  LicenseManager,
  ModuleRegistry,
} from 'ag-grid-enterprise'
import 'ag-grid-enterprise/styles/ag-grid.css'
import 'ag-grid-enterprise/styles/ag-theme-quartz.css'

import type React from 'react'

import { useTheme } from '@moment/design-system-ui/theme'
import { AgGridReact } from 'ag-grid-react'
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'

import { MomentEnv } from '@/app/moment-env'
import Checkbox from '@/components/atomic/checkbox'
import { Mode } from '@/types/enums/mode'

import {
  type TableContext,
  TableDataModel,
  type TableProps,
  TableSize,
} from './types'
import { getCheckboxCellRenderer } from './utils'

import './table.css'

const PAGE_SIZE = 100
const QUICK_FILTER_TEXT = 'quickFilterText'

ModuleRegistry.registerModules([AllEnterpriseModule])

LicenseManager.setLicenseKey(MomentEnv.AgGridLicenseKey || '')

if (MomentEnv.AgGridTestIdsEnabled) {
  setupAgTestIds()
}

/**
 * This pattern should go away - we're violating React data flow principles:
 * https://react.dev/learn/referencing-values-with-refs#best-practices-for-refs
 * https://legacy.reactjs.org/docs/refs-and-the-dom.html#dont-overuse-refs
 *
 * So how do we get access to the grid api then?
 * https://stackoverflow.com/a/61990755
 *
 * Consumers should be able to use this directly without having to expose something like this
 */
export interface TableImperativeHandleRef {
  clearSelectedRows: () => void
  getRowNodeById?: (id: string) => IRowNode | undefined
  removeRowNodes?: (rowNodes: IRowNode[]) => void
  addRowNodes?: (rowNodes: IRowNode[]) => void
  refreshCells?: () => void
  exportDataAsCsv?: (params?: CsvExportParams) => void
  getDataAsCsv?: () => string | undefined
}

// Mark all grids as using legacy themes (migration to 35.x)
// https://www.ag-grid.com/react-data-grid/upgrading-to-ag-grid-33/#continue-with-legacy-themes
provideGlobalGridOptions({ theme: 'legacy' })

function Table<T extends object>(
  {
    tableSize,
    customRowHeight,
    headerHeight,
    gridModel,
    data,
    columnDefs,
    checkboxSelection = false,
    autoGroupColumnDef,
    createServerSideDatasource,
    mode,
    onCellClicked,
    onCellHover,
    onColumnMoved,
    onColumnResized,
    onSelectedRowsChange,
    searchTerm,
    gridOptions,
    loading,
    useTransactionAPI = false,
    pagination = true,
    debug = false,
    onExternalUpdate,
    externalUpdateVersion,
    idConfig,
    isCheckboxDisabled,
    getCheckboxTooltip,
    onCellEditingStopped,
    onCellValueChanged,
    onSortChanged,
    groupDefaultExpanded,
    groupHeaderHeight,
    suppressAggFuncInHeader = false,
    suppressColumnVirtualisation = false,
    suppressExportFromContextMenu = false,
    showHeaderFilterButtons = false,
    hideSelectAll = false,
    processUnpinnedColumns,
    suppressServerSideFullWidthLoadingRow,
    ...restGridProps
  }: TableProps<T>,
  ref: React.Ref<TableImperativeHandleRef>,
) {
  //
  // Grid state
  //

  // biome-ignore lint/correctness/useHookAtTopLevel: biome now sees props, ref (correctly) as invalid component signature - can refactor to remove forwardref
  const gridRef = useRef<AgGridReact | null>(null)
  // biome-ignore lint/correctness/useHookAtTopLevel: biome now sees props, ref (correctly) as invalid component signature - can refactor to remove forwardref
  const [gridApi, setGridApi] = useState<GridApi | null>(null)
  // biome-ignore lint/correctness/useHookAtTopLevel: biome now sees props, ref (correctly) as invalid component signature - can refactor to remove forwardref
  const context = useMemo<TableContext>((): TableContext => {
    return {
      tableSize,
      checkboxState: {
        selectedRowIds: new Set<string>(),
        headerCheckboxChecked: false,
      },
    }
  }, [tableSize])

  // Merge gridOptions.context with local context
  // biome-ignore lint/correctness/useHookAtTopLevel: biome now sees props, ref (correctly) as invalid component signature - can refactor to remove forwardref
  const mergedContext = useMemo(
    () => ({ ...gridOptions?.context, ...context }),
    [gridOptions?.context, context],
  )

  //
  // Themeing
  //

  // Fall back to specified mode if not provided by hook
  // biome-ignore lint/correctness/useHookAtTopLevel: biome now sees props, ref (correctly) as invalid component signature - can refactor to remove forwardref
  const { theme: resolvedTheme = mode } = useTheme()
  const themeClass =
    resolvedTheme === Mode.Dark ? 'ag-theme-quartz-dark' : 'ag-theme-quartz' // App defaults to light theme when theme not specified. AG Grid matches it.

  //
  // Checkbox selection
  //

  // biome-ignore lint/correctness/useHookAtTopLevel: biome now sees props, ref (correctly) as invalid component signature - can refactor to remove forwardref
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set())
  // biome-ignore lint/correctness/useHookAtTopLevel: biome now sees props, ref (correctly) as invalid component signature - can refactor to remove forwardref
  const [headerCheckboxChecked, setHeaderCheckboxChecked] = useState<
    boolean | 'indeterminate'
  >(false)

  // Mutate the stable context object so checkbox renderers read current
  // state without recreating the column def (which would reset AG Grid sort).
  context.checkboxState.selectedRowIds = selectedRowIds
  context.checkboxState.headerCheckboxChecked = headerCheckboxChecked

  // biome-ignore lint/correctness/useHookAtTopLevel: biome now sees props, ref (correctly) as invalid component signature - can refactor to remove forwardref
  const bulkUpdateSelectedRows = useCallback(
    (value: boolean | 'indeterminate') => {
      if (value === true) {
        const selected = new Set<string>()
        // If there are grouped rows in the table, bulk selection will cause the
        //  child rows to be selected as well. We use the `isChildRow` flag to
        //  skip selection of child rows, also we wanto to prevent selection of
        //  rows that are not selectable
        gridApi?.forEachNodeAfterFilter((node) => {
          const isChildRow = node.data?.isChildRow
          if (isChildRow || isCheckboxDisabled?.(node)) return
          // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
          selected.add(node.id!)
        })
        setSelectedRowIds(selected)
        setHeaderCheckboxChecked(true)
      } else {
        setSelectedRowIds(new Set())
        setHeaderCheckboxChecked(false)
      }
    },
    [gridApi, isCheckboxDisabled],
  )

  // biome-ignore lint/correctness/useHookAtTopLevel: biome now sees props, ref (correctly) as invalid component signature - can refactor to remove forwardref
  useEffect(() => {
    if (gridApi) {
      // Total number of selectable rows may be different than the total number of displayed rows (e.g. some may be GPOs thus not groupable)
      // then the number of displayed rows may be different than the total number of rows if a row is grouped all of its child rows aren't selectable
      // but still count for ag-grid
      // TODO: improve performance on this, likely by precalculating it, we only need the number not an array of ids
      let selectableRows = 0
      if (gridModel === TableDataModel.ServerSide) {
        selectableRows = gridApi.getDisplayedRowCount()
      } else {
        // forEachNodeAfterFilter can only be called when rowModelType is clientSide
        gridApi.forEachNodeAfterFilter((node) => {
          if (isCheckboxDisabled?.(node)) return
          selectableRows++
        })
      }

      let newChecked: boolean | 'indeterminate'
      if (selectedRowIds.size === 0) {
        newChecked = false
      } else if (selectableRows === selectedRowIds.size) {
        newChecked = true
      } else {
        newChecked = 'indeterminate'
      }
      setHeaderCheckboxChecked(newChecked)

      // Update mutable context so AG Grid renderers see the latest state
      // when we trigger a refresh below (React setState is async).
      context.checkboxState.selectedRowIds = selectedRowIds
      context.checkboxState.headerCheckboxChecked = newChecked
      gridApi.refreshCells({ columns: ['checkbox'], force: true })
      gridApi.refreshHeader()

      onSelectedRowsChange?.(selectedRowIds)
    }
  }, [
    selectedRowIds,
    gridApi,
    gridModel,
    onSelectedRowsChange,
    isCheckboxDisabled,
    context,
  ])

  // biome-ignore lint/correctness/useHookAtTopLevel: biome now sees props, ref (correctly) as invalid component signature - can refactor to remove forwardref
  const checkboxColumnDef = useMemo<ColDef[]>(() => {
    return getCheckboxColumnDef(
      checkboxSelection,
      bulkUpdateSelectedRows,
      setSelectedRowIds,
      isCheckboxDisabled,
      getCheckboxTooltip,
      hideSelectAll,
    )
  }, [
    checkboxSelection,
    bulkUpdateSelectedRows,
    isCheckboxDisabled,
    getCheckboxTooltip,
    hideSelectAll,
  ])

  // biome-ignore lint/correctness/useHookAtTopLevel: biome now sees props, ref (correctly) as invalid component signature - can refactor to remove forwardref
  const getRowNodeById = useCallback(
    (id: string) => {
      if (!gridApi) return undefined
      return gridApi.getRowNode(id)
    },
    [gridApi],
  )

  // biome-ignore lint/correctness/useHookAtTopLevel: biome now sees props, ref (correctly) as invalid component signature - can refactor to remove forwardref
  const removeRowNodes = useCallback(
    (rowNodes: IRowNode[]) => {
      if (!gridApi) return
      gridApi.applyTransaction({
        remove: rowNodes,
      })
    },
    [gridApi],
  )

  // biome-ignore lint/correctness/useHookAtTopLevel: biome now sees props, ref (correctly) as invalid component signature - can refactor to remove forwardref
  const addRowNodes = useCallback(
    (rowNodes: IRowNode[]) => {
      if (!gridApi) return
      gridApi.applyTransaction({ add: rowNodes })
    },
    [gridApi],
  )

  // biome-ignore lint/correctness/useHookAtTopLevel: biome now sees props, ref (correctly) as invalid component signature - can refactor to remove forwardref
  const refreshCells = useCallback(() => {
    if (!gridApi) return
    gridApi.refreshCells({ force: true })
  }, [gridApi])

  // Expose handlers to parent (violate React data flow)
  // biome-ignore lint/correctness/useHookAtTopLevel: biome now sees props, ref (correctly) as invalid component signature - can refactor to remove forwardref
  useImperativeHandle(ref, () => ({
    clearSelectedRows: () => {
      setSelectedRowIds(new Set())
      setHeaderCheckboxChecked(false)
    },
    getRowNodeById,
    removeRowNodes,
    addRowNodes,
    refreshCells,
    exportDataAsCsv: (params?: CsvExportParams) => {
      if (gridApi) gridApi.exportDataAsCsv(params)
    },
  }))

  //
  // Column definitions and grid configurations
  //

  // biome-ignore lint/correctness/useHookAtTopLevel: biome now sees props, ref (correctly) as invalid component signature - can refactor to remove forwardref
  const defaultColDef = useMemo<ColDef>(() => {
    return {
      flex: 1,
      wrapHeaderText: true,
      filter: false,
      sortable: true,
      suppressHeaderMenuButton: true,
      // TODO [Christian]: Uncomment floatingFilter and filter when we have fix for broken test
      // floatingFilter: true,
      // floatingFilterComponentParams: {
      //   suppressFilter: true,
      // },
      cellClass: tableSize === TableSize.Full ? 'full-cell' : 'compact-cell',
      headerClass:
        tableSize === TableSize.Full ? 'full-header' : 'compact-header',
      icons: {
        checkboxUnchecked: () => <Checkbox />,
      },
    }
  }, [tableSize])

  // biome-ignore lint/correctness/useHookAtTopLevel: biome now sees props, ref (correctly) as invalid component signature - can refactor to remove forwardref
  const getRowId = useCallback(
    (params: GetRowIdParams) => {
      const {
        clientSideField,
        serverSideField,
        fallback = 'id',
      } = idConfig || {}

      const idField =
        gridModel === TableDataModel.ClientSide
          ? clientSideField || fallback
          : serverSideField || fallback

      const id = params.data[idField]

      if (!id) {
        throw new Error(
          `Row data must supply '${idField}' field for ${
            gridModel === TableDataModel.ClientSide
              ? 'client-side'
              : 'server-side'
          } data`,
        )
      }

      return id
    },
    [gridModel, idConfig],
  )

  // biome-ignore lint/correctness/useHookAtTopLevel: biome now sees props, ref (correctly) as invalid component signature - can refactor to remove forwardref
  const augmentedColumnDefs = useMemo(() => {
    return checkboxColumnDef.concat(columnDefs)
  }, [columnDefs, checkboxColumnDef])

  // biome-ignore lint/correctness/useHookAtTopLevel: biome now sees props, ref (correctly) as invalid component signature - can refactor to remove forwardref
  const augmentedGridOptions = useMemo(() => {
    return {
      ...gridOptions,
      debug,
    }
  }, [gridOptions, debug])

  //
  // Grid lifecycle
  //

  // When externalUpdateVersion changes, call the onExternalUpdate callback
  // biome-ignore lint/correctness/useExhaustiveDependencies: Bulk suppression as part of biome migration
  // biome-ignore lint/correctness/useHookAtTopLevel: biome now sees props, ref (correctly) as invalid component signature - can refactor to remove forwardref
  useEffect(() => {
    if (gridApi && onExternalUpdate) {
      onExternalUpdate(gridApi)
    }
  }, [onExternalUpdate, externalUpdateVersion, gridApi])

  // biome-ignore lint/correctness/useExhaustiveDependencies: Bulk suppression as part of biome migration
  // biome-ignore lint/correctness/useHookAtTopLevel: biome now sees props, ref (correctly) as invalid component signature - can refactor to remove forwardref
  const onGridReady = useCallback(
    (event: GridReadyEvent) => {
      // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
      const gridApi = gridRef.current!.api
      setGridApi(gridApi)
      if (
        gridModel === TableDataModel.ServerSide &&
        createServerSideDatasource
      ) {
        gridApi.setGridOption(
          'serverSideDatasource',
          createServerSideDatasource(),
        )
      }

      if (gridApi && gridModel === TableDataModel.ClientSide) {
        gridApi.setGridOption(QUICK_FILTER_TEXT, searchTerm || undefined)
        onExternalUpdate?.(gridApi)
      }

      // Call the onGridReady callback from gridOptions if it exists
      gridOptions?.onGridReady?.(event)
    },
    [
      createServerSideDatasource,
      gridModel,
      searchTerm,
      onExternalUpdate,
      setGridApi,
      gridOptions,
    ],
  )

  // biome-ignore lint/correctness/useHookAtTopLevel: biome now sees props, ref (correctly) as invalid component signature - can refactor to remove forwardref
  useEffect(() => {
    if (
      gridApi &&
      gridModel === TableDataModel.ServerSide &&
      createServerSideDatasource
    ) {
      gridApi.setGridOption(
        'serverSideDatasource',
        createServerSideDatasource(),
      )
    }
  }, [createServerSideDatasource, gridModel, gridApi])

  // biome-ignore lint/correctness/useHookAtTopLevel: biome now sees props, ref (correctly) as invalid component signature - can refactor to remove forwardref
  const isLoading = useMemo(() => {
    return gridModel === TableDataModel.ServerSide ? undefined : loading
  }, [gridModel, loading])

  //
  // Event handlers
  //

  // biome-ignore lint/correctness/useHookAtTopLevel: biome now sees props, ref (correctly) as invalid component signature - can refactor to remove forwardref
  const handleColumnResized = useCallback(
    (event: ColumnResizedEvent) => {
      if (event.finished && onColumnResized && event.source !== 'flex') {
        onColumnResized(event)
      }
    },
    [onColumnResized],
  )

  // biome-ignore lint/correctness/useHookAtTopLevel: biome now sees props, ref (correctly) as invalid component signature - can refactor to remove forwardref
  const handleColumnMoved = useCallback(
    (event: ColumnMovedEvent) => {
      if (event.finished && onColumnMoved) {
        onColumnMoved(event)
      }
    },
    [onColumnMoved],
  )

  // biome-ignore lint/correctness/useHookAtTopLevel: biome now sees props, ref (correctly) as invalid component signature - can refactor to remove forwardref
  const onFilterTextBoxChanged = useCallback(() => {
    if (gridModel === TableDataModel.ServerSide) {
      return
    }
    if (gridApi) {
      gridApi.setGridOption(QUICK_FILTER_TEXT, searchTerm || undefined)
    }
  }, [gridModel, searchTerm, gridApi])

  // biome-ignore lint/correctness/useHookAtTopLevel: biome now sees props, ref (correctly) as invalid component signature - can refactor to remove forwardref
  useEffect(() => {
    onFilterTextBoxChanged()
  }, [onFilterTextBoxChanged])

  // biome-ignore lint/correctness/useHookAtTopLevel: biome now sees props, ref (correctly) as invalid component signature - can refactor to remove forwardref
  const handleCellClicked = useCallback(
    (params: CellClickedEvent) => {
      // Ignore clicks in the checkbox column
      if (params.column.getColId() === 'checkbox') {
        return
      }
      // Ignore clicks on loading skeleton rows (no data)
      if (!params.data) {
        return
      }
      // Ignore clicks on columns that have mouse event handling suppressed
      if (params.colDef.cellRendererParams?.suppressMouseEventHandling) {
        return
      }
      onCellClicked?.(params)
    },
    [onCellClicked],
  )

  return (
    <div
      className={`h-full w-full legacy-table grid-container ${themeClass} ${tableSize === TableSize.Full ? 'full-table' : 'compact-table'}`}
    >
      <AgGridReact
        className={showHeaderFilterButtons ? '' : 'hide-filter-button'}
        // Initializations
        ref={gridOptions?.context?.omitRef ? undefined : gridRef}
        context={mergedContext}
        onGridReady={onGridReady}
        // Client/server-side configurations
        rowData={
          gridModel === TableDataModel.ClientSide && !useTransactionAPI
            ? data
            : undefined
        }
        rowModelType={
          gridModel === TableDataModel.ClientSide ? 'clientSide' : 'serverSide'
        }
        getRowId={getRowId}
        suppressServerSideFullWidthLoadingRow={
          suppressServerSideFullWidthLoadingRow ??
          // biome-ignore lint/complexity/noUselessTernary: Bulk suppression as part of biome migration
          (gridModel === TableDataModel.ServerSide ? true : false)
        }
        serverSideInitialRowCount={
          gridModel === TableDataModel.ServerSide
            ? (gridOptions?.serverSideInitialRowCount ?? PAGE_SIZE)
            : undefined
        }
        cacheBlockSize={
          gridModel === TableDataModel.ServerSide
            ? (gridOptions?.cacheBlockSize ?? PAGE_SIZE)
            : undefined
        }
        rowBuffer={gridModel === TableDataModel.ClientSide ? 20 : undefined}
        // Columns
        columnDefs={augmentedColumnDefs}
        defaultColDef={defaultColDef}
        // Grouping configs
        autoGroupColumnDef={autoGroupColumnDef}
        groupDisplayType={'singleColumn'}
        groupDefaultExpanded={groupDefaultExpanded}
        groupAllowUnbalanced={true}
        gridOptions={augmentedGridOptions}
        // AG Grid: As of v33, suppressRowGroupHidesColumns is deprecated. Use `suppressGroupChangesColumnVisibility: "suppressHideOnGroup"` instead.
        suppressGroupChangesColumnVisibility={'suppressHideOnGroup'}
        suppressColumnVirtualisation={suppressColumnVirtualisation}
        // Height and width configs
        rowHeight={customRowHeight || (tableSize === TableSize.Full ? 40 : 32)}
        headerHeight={headerHeight ?? (tableSize === TableSize.Full ? 34 : 28)}
        groupHeaderHeight={
          groupHeaderHeight ??
          headerHeight ??
          (tableSize === TableSize.Full ? 34 : 28)
        }
        // Sorting configs
        suppressMultiSort={true}
        // Row selection configs
        onCellClicked={handleCellClicked}
        onCellMouseOver={onCellHover}
        onCellEditingStopped={onCellEditingStopped}
        // Pagination configs
        pagination={pagination}
        paginationPageSize={PAGE_SIZE}
        paginationPageSizeSelector={false}
        // Column alteration configs
        onColumnMoved={handleColumnMoved}
        onColumnResized={handleColumnResized}
        // Loading configs
        loading={isLoading}
        onCellValueChanged={onCellValueChanged}
        onSortChanged={onSortChanged}
        suppressCsvExport={suppressExportFromContextMenu}
        suppressExcelExport={suppressExportFromContextMenu}
        suppressAggFuncInHeader={suppressAggFuncInHeader}
        processUnpinnedColumns={processUnpinnedColumns}
        {...restGridProps}
      />
    </div>
  )
}

function getCheckboxColumnDef(
  checkboxSelection: boolean,
  bulkUpdateSelectedRows: (value: boolean | 'indeterminate') => void,
  setSelectedRowIds: React.Dispatch<React.SetStateAction<Set<string>>>,
  isCheckboxDisabled?: (row: IRowNode) => boolean,
  getCheckboxTooltip?: (row: IRowNode) => string | undefined,
  hideSelectAll?: boolean,
): ColDef[] {
  if (!checkboxSelection) return []

  return [
    {
      field: 'checkbox',
      headerName: '',
      headerComponent: hideSelectAll
        ? undefined
        : (headerParams: { context?: TableContext }) => {
            const checked =
              headerParams.context?.checkboxState.headerCheckboxChecked ?? false
            return getCheckboxCellRenderer(bulkUpdateSelectedRows, checked)
          },
      cellRenderer: (params: ICellRendererParams) => {
        const checkboxState = (params.context as TableContext | undefined)
          ?.checkboxState
        const onCheckedChange = (value: boolean | 'indeterminate') => {
          setSelectedRowIds((prevSelectedRowIds: Set<string>) => {
            const selected = new Set(prevSelectedRowIds)
            const nodeId = params.node.id
            if (!nodeId) return selected
            if (value === true) {
              selected.add(nodeId)
            } else if (value === false) {
              selected.delete(nodeId)
            }
            return selected
          })
        }
        const isChecked = params.node.id
          ? (checkboxState?.selectedRowIds.has(params.node.id) ?? false)
          : false
        const isDisabled = isCheckboxDisabled?.(params.node)
        const tooltipText = getCheckboxTooltip?.(params.node)

        return getCheckboxCellRenderer(
          onCheckedChange,
          isChecked,
          params.data?.isChildRow,
          isDisabled,
          tooltipText,
        )
      },
      minWidth: 50,
      maxWidth: 50,
      // with these two props, we prevent the checkbox column from being
      // moved or dragged
      lockPosition: true,
      suppressMovable: true,
      // with this one we simply anchor it as the leftmost column
      // we should not have other columns pinned to the left, since
      // the appearance of the checkbox column is conditional and we
      // don't pin any other columns to the left, we will be good
      pinned: 'left',
      sortable: false,
    },
  ]
}

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default memo(forwardRef(Table))
